import { Injectable } from '@angular/core';
import { CollectionItem } from '../interfaces/collection-item.interface';
import { ProcessedNode } from '../interfaces/processed-node.interface';
import { cloneDeep, flattenDeep } from 'lodash';
import { MovedItemMetadata } from '../interfaces/moved-item-metadata.interface';
import { VIRTUAL_FOLDER_ID } from '../folders-constants';
import { CollectionItemMetadata } from '../interfaces/collection-item-metadata.interface';
import { CollectionItemType } from '../enums/collection-item-type.enum';

@Injectable({
  providedIn: 'root',
})
export class FoldersUtilsService {
  /*
    TODO: rewrite data typings to generics for allowing to work not only with CollectionItem, but also with objects,
     that have SubFolders property
  */

  addNestingLevels(nodes: CollectionItem[], startLevel = 1): ProcessedNode[] {
    const copy: ProcessedNode[] = JSON.parse(JSON.stringify(nodes));

    const process = (nodesList: ProcessedNode[], currentLevel: number) => {
      for (const node of nodesList) {
        node.Level = currentLevel;
        if (node.SubFolders.length) {
          process(node.SubFolders, currentLevel + 1);
        }
      }
    };

    process(copy, startLevel);

    return copy;
  }

  updateElement(nodes: ProcessedNode[], targetId: string, changes: Partial<ProcessedNode>): ProcessedNode[] {
    return nodes.map(node => {
      if (node.Id === targetId) {
        return { ...node, ...changes };
      }

      if (node.SubFolders.length) {
        const newChildren = this.updateElement(node.SubFolders, targetId, changes);

        if (newChildren !== node.SubFolders) {
          return { ...node, SubFolders: newChildren };
        }
      }

      return node;
    });
  }

  removeElement(nodes: ProcessedNode[], targetId: string) {
    return nodes
      .filter(node => node.Id !== targetId)
      .map(node => {
        if (node.SubFolders.length) {
          const newChildren = this.removeElement(node.SubFolders, targetId);

          return newChildren !== node.SubFolders ? { ...node, SubFolders: newChildren } : node;
        }

        return node;
      });
  }

  getElementById(nodes: ProcessedNode[], targetId: string | null): ProcessedNode | undefined {
    if (targetId === null) {
      return undefined;
    }

    const findNodeRecursive = (items: ProcessedNode[]): ProcessedNode | undefined => {
      for (const node of items) {
        if (node.Id === targetId) {
          return node;
        }

        if (node.SubFolders.length) {
          const foundInChildren = findNodeRecursive(node.SubFolders);

          if (foundInChildren) {
            return foundInChildren;
          }
        }
      }

      return undefined;
    };

    return findNodeRecursive(nodes);
  }

  getAllNodeChildren(node: ProcessedNode): ProcessedNode[] {
    return node.SubFolders?.reduce((acc, child) => {
      acc.push(child);
      acc.push(...this.getAllNodeChildren(child));

      return acc;
    }, [] as ProcessedNode[]);
  }

  getAllChildren(nodes: ProcessedNode[]): ProcessedNode[] {
    const items = nodes.map(node => this.getAllNodeChildren(node));

    return flattenDeep(items);
  }

  getAll(nodes: ProcessedNode[]): ProcessedNode[] {
    const flattenedChildren = this.getAllChildren(nodes);

    return [...nodes, ...flattenedChildren];
  }

  isMoveValid(nodes: ProcessedNode[], sourceId: string, targetId: string): boolean {
    const sourceNode = this.getElementById(nodes, sourceId);
    if (!sourceNode) {
      return false;
    }

    const isTargetChild = this.checkForDescendant(sourceNode, targetId);

    return !isTargetChild;
  }

  checkForDescendant(node: ProcessedNode, targetId: string): boolean {
    if (node.Id === targetId) {
      return true;
    }

    for (const child of node.SubFolders) {
      if (this.checkForDescendant(child, targetId)) {
        return true;
      }
    }

    return false;
  }

  getMaxTreeDepth(nodes: ProcessedNode[]): number {
    if (nodes.length === 0) {
      return 0;
    }

    return Math.max(
      ...nodes.map(node => {
        const childrenDepth = this.getMaxTreeDepth(node.SubFolders);

        return childrenDepth + 1;
      }),
    );
  }

  getMovedItems(
    preselectedItemMetadata: CollectionItemMetadata[],
    targetId: string,
    type: CollectionItemType,
    parentFolderId: string,
  ): {
    [key: string]: MovedItemMetadata;
  } {
    const sourceArray = preselectedItemMetadata.some(item => item.Id === targetId)
      ? preselectedItemMetadata
      : [
          {
            Id: targetId,
            Type: type,
          },
        ];

    return sourceArray.reduce(
      (acc, item): { [key: string]: MovedItemMetadata } => ({
        ...acc,
        [item.Id]: {
          ParentFolderId: parentFolderId === VIRTUAL_FOLDER_ID ? null : parentFolderId,
          Type: item.Type,
        },
      }),
      {},
    );
  }

  replaceNode(nodes: ProcessedNode[], newNode: ProcessedNode): ProcessedNode[] {
    return nodes.map(node => {
      if (node.Id === newNode.Id) {
        return newNode;
      }

      if (node.SubFolders?.length) {
        const newChildren = this.replaceNode(node.SubFolders, newNode);

        return newChildren !== node.SubFolders ? { ...node, SubFolders: newChildren } : node;
      }

      return node;
    });
  }

  findParentId(nodes: ProcessedNode[], targetId: string): string | null {
    const search = (currentNodes: ProcessedNode[], parentId: string | null): string | null => {
      for (const node of currentNodes) {
        if (node.Id === targetId) {
          return parentId;
        }

        if (node.SubFolders?.length) {
          const found = search(node.SubFolders, node.Id);
          if (found !== null) {
            return found;
          }
        }
      }
      return null;
    };

    return search(nodes, null);
  }

  sortFolders(nodes: ProcessedNode[]): ProcessedNode[] {
    if (!nodes.length) {
      return nodes;
    }

    const nodesClone = cloneDeep(nodes);
    return nodesClone.sort((a, b) => {
      if (a.Name.toLowerCase() < b.Name.toLowerCase()) {
        return -1;
      }
      if (a.Name.toLowerCase() > b.Name.toLowerCase()) {
        return 1;
      }

      return 0;
    });
  }
}
